<script lang="ts" setup>
import { useIntersectionObserver, useOffsetPagination } from '@vueuse/core'
import type { BlokArticleList } from '~~/types/storyblok/bloks'
import type { ContentTypeArticleStoryList } from '~~/types/storyblok/stories'
import type { Ref } from 'vue'

const props = defineProps<{ blok: BlokArticleList }>()

const queryOptions = computed(() => {
  return {
    content_type: 'ContentTypeArticle',
    starts_with: 'insights/article',
    per_page: pageSize.value,
    sort_by: 'first_published_at:desc',
  }
})

const pageSize: Ref<number> = ref((props.blok.quantity as number) || 12)

const isLoading = ref(false)
const filter = ref({})
const totalArticles = ref(0)
const currentPage = ref(1)

const fetchData = async () => {
  isLoading.value = true
  const response = await useAsyncStoryblokStoryQuery({
    ...queryOptions.value,
    ...filter.value,
    page: currentPage.value,
  })
  isLoading.value = false
  totalArticles.value = response.value.total
  return response.value.data.stories as ContentTypeArticleStoryList
}

const updateData = async () => {
  const response = await fetchData()
  if (currentPage.value === 1) {
    storyData.value = response
  } else {
    storyData.value.push(...response)
  }
}

const storyData: Ref<ContentTypeArticleStoryList> = ref(await fetchData())

const { isLastPage, next } = useOffsetPagination({
  total: totalArticles,
  page: currentPage,
  pageSize,
  onPageChange: updateData,
  onPageSizeChange: updateData,
})

const infiniteContainer = ref<HTMLElement>()
useIntersectionObserver(
  infiniteContainer,
  // load more
  (entry) => {
    if (entry[0].isIntersecting && !isLoading.value) {
      next()
    }
  },
  {
    threshold: 1.0,
  }
)

const handleFilterChange = (filterData: { [key: string]: string }) => {
  filter.value = filterData
  currentPage.value = 1
}
</script>

<template lang="pug">
div(v-editable="blok")
  ArticleFilter(@filter-changed="handleFilterChange")

  div(class="blok-article-list")
    div(class="container grid grid-cols-12 gap-18 md:gap-24")
      ArticleCard(
        v-for="article in storyData"
        :key="article.uuid"
        :title="article.name"
        :path="article.full_slug"
        :blok="article.content"
        class="w-auto col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3"
      )
      div(
        v-if="!isLastPage || isLoading"
        ref="infiniteContainer"
        class="col-span-full text-center"
      ) Loading...
</template>
